import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { FormControl, Grid, InputAdornment, Link, OutlinedInput, Paper, Stack, styled, Table, TableHead, TableBody, TableCell, TableContainer, TableRow, Tooltip, Chip, Button, CircularProgress, Box } from '@mui/material';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { Search } from '@mui/icons-material';
import EmailRecord from '../../../dataObjects/EmailRecord';
import EmailLog from '../../../dataObjects/EmailLog';

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const FailedEmailList = () => {

    const [searchInput, setSearchInput] = useState('');
    const [failedEmails, setFailedEmails] = useState<EmailRecord[]>();
    const [allEmails, setAllEmails] = useState<EmailRecord[]>();

    //loading indicator
    const [isLoading, setIsLoading] = useState(false);

    const [emailRemove, setEmailRemove] = useState('')

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    //payload
    useEffect(() => {
        getFailedEmailList();
    }, [])

    const getFailedEmailList = async () => {
        await fetch(`/ItDashboard/getFailedEmails`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: ItDashboard - GetFailedEmails',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    setIsLoading(false);
                }
                else {
                    setIsLoading(false);
                    setFailedEmails(result);
                    if (!searchInput)
                        setAllEmails(result);
                }
            }
            )
    }

    const getMatchedEmails = async (searchInput: string) => {
        setIsLoading(true);
        await fetch(`/ItDashboard/GetMatchedEmails?emailPhrase=${searchInput}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: ItDashboard - GetMatchedEmails',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    setIsLoading(false);
                }
                else {
                    setIsLoading(false);
                    setFailedEmails(result);
                    if (!searchInput)
                        setAllEmails(result);
                }
            }
        )
    }

    const checkSuppression = async (email: string) => {
        await fetch(`/ItDashboard/CheckSuppression?email=${email}`)
            .then(result => result.json())
            .then(result => {
                if (result.success) {
                    let info = {
                        type: 'confirm',
                        title: "Confirm: Found in Suppression List",
                        buttonLabel: "Yes",
                        errorMessage: `This email (${email}) is in OCPF suppression list: ${result.message}!  Would you like to remove this email from the list?`,
                        secondaryErrorMessage: ''
                    };
                    setEmailRemove(email);
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'warning',
                        title: 'Warning - Not Found',
                        errorMessage: result.message,
                        secondaryErrorMessage: ""
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
            }
        )
    }

    const removeSuppression = async (email: string) => {
        await fetch(`/ItDashboard/RemoveSuppression?email=${email}`)
            .then(result => result.json())
            .then(result => {
                if (!result.success) {
                    let info = {
                        type: 'error',
                        title: 'Error: ItDashboard - RemoveSuppression',
                        errorMessage: result.message,
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'Email Removed',
                        errorMessage: 'Email successfully removed from suppression list',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
            }
       )
    }

    const confirmAction = (confirm: boolean) => {
        if (confirm) {
            removeSuppression(emailRemove);
        }
        else {
            setAlertInfoDialog(false);
        }
    }

    const updateSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        setTimeout(() => {
            let searchPhrase = event.target.value;
            if (searchPhrase === '') {
                setFailedEmails(allEmails);
                setIsLoading(false);
            }
            else {
                getMatchedEmails(searchPhrase);
            }
        }, 500)
    }

    const displayEmailStatus = (events: EmailLog[]) => {

        let failedEvent = events.find((event: EmailLog) => event.eventType === "failed");
        let deliveredEvent = events.find((event: EmailLog) => event.eventType === "delivered"); 

        if (deliveredEvent) {
            return (
                <TableCell colSpan={2}><Chip label={'delivered'} variant="outlined" size="small" color="success" /></TableCell>
            );
        }

        if (failedEvent)
            return (
                <>
                    <TableCell>
                        <Stack direction="row" spacing={1}>
                            <Chip label={'failed'} variant="outlined" size="small" color="error" />
                            <Typography variant="body2" color={"error"} >{failedEvent.deliveryStatusMessage}</Typography>
                        </Stack> 
                    </TableCell>
                    <TableCell>
                        <Button variant="outlined" color="primary" size="small" onClick={()=>checkSuppression(failedEvent?failedEvent.recipientEmailAddress:"")}>Check</Button>
                    </TableCell>
                </> 
            );
        return (<TableCell colSpan={2}></TableCell>);
    }

    const displayFormatedDate = (eventDate: string) => {

        const date = new Date(eventDate);
        const formatted = new Intl.DateTimeFormat("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true
        }).format(date);

        return formatted;
    }



    return (
        <>
            <Grid item xs={12} marginTop={2} >
                <Stack direction="row" spacing={3} marginBottom={3}>
                    <Typography color={'error'} variant="subtitle1">
                        Recent Failed Emails
                    </Typography>
                    <FormControl sx={{ minWidth: 300, marginBottom: "15px" }} variant="outlined" size="small">
                        <OutlinedInput
                            aria-label='search emails...'
                            title='search emails...'
                            placeholder={"Search email..."}
                            onChange={updateSearchInput}
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Stack>
                
                {
                    failedEmails && failedEmails.length > 0 &&
                    <TableContainer sx={{ maxHeight: 600 }} component={Paper}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#FFFFFF' }}>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Filer Name</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Recipient Email</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Email Subject</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Date Sent</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Email Status</Typography></TableCell>
                                        <TableCell>Suppressions?</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                   {failedEmails.map((email: EmailRecord, index) =>
                                    <StyledTableRow key={index}>
                                         <TableCell width="15%">
                                               <Typography variant="body2"><Link href={`/Filers?q=${email.cpfId}`} target="_blank">{email.cpfId}</Link> {email.filerName}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`${email.recipientEmailAddress}`} placement="top">
                                                <Typography style={{ width: 200 }} variant="body2" noWrap>{email.recipientEmailAddress}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                         <TableCell>
                                            <Tooltip title={`${email.subject}`} placement="top">
                                                <Typography style={{ width: 150 }} variant="body2" noWrap>{email.subject}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                               <Typography variant="body2" style={{ width: 150 }}><i>{displayFormatedDate(email.date)}</i></Typography>
                                         </TableCell>
                                         {displayEmailStatus(email.emailEvents)}
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog} confirmAction={confirmAction}></AlertInfoDialog>
            }
            {
                isLoading &&
                <CircularProgress
                    size={50}
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "90%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 2
                    }}
                />
            }
        </>
    );
}

export default FailedEmailList 